import { useMemo } from 'react';

import type { TimeFormat } from '@app/api/Branches/types';

import {
  formatTime,
  formatTimelineLabel,
  toMinutes,
} from '@pages/Calendar/utils';

export type TimelineMarkType = 'hour' | 'half-hour' | 'quarter';
export type TimelineMarkKind = 'boundary-start' | 'regular';

export interface TimelineMark {
  time: string;
  label: string;
  kind: TimelineMarkKind;
  type: TimelineMarkType;
  // True when the label uses full HH:mm to anchor a timeline that doesn't start
  // on an hour boundary — must render with the primary label styling.
  isAnchorLabel: boolean;
}

export interface TimelineBoundaryMark {
  time: string;
  label: string;
  type: TimelineMarkType;
  offsetPercent: number;
}

interface UseCalendarTimelineProps {
  startTime: string;
  endTime: string;
  stepMinutes: number;
  boundaryEndTime?: string;
  // Формат журнала филиала (JournalFormatTime). Влияет только на подписи:
  // поле `time` остаётся в HH:mm — по нему считают минуты (issue/11162).
  timeFormat?: TimeFormat | null;
}

const resolveMarkType = (totalMinutes: number): TimelineMarkType => {
  if (totalMinutes % 60 === 0) return 'hour';
  if (totalMinutes % 30 === 0) return 'half-hour';
  return 'quarter';
};

const resolveMarkLabel = (
  totalMinutes: number,
  type: TimelineMarkType,
  timeFormat: TimeFormat | null | undefined
): string => {
  if (type === 'hour') return formatTimelineLabel(totalMinutes, timeFormat);
  if (type === 'half-hour') return '30';
  return '';
};

export const useCalendarTimeline = ({
  startTime,
  endTime,
  stepMinutes,
  boundaryEndTime,
  timeFormat,
}: UseCalendarTimelineProps) => {
  const boundaryEndMark = useMemo<TimelineBoundaryMark | undefined>(() => {
    const startTotalMinutes = toMinutes(startTime);
    const endTotalMinutes = toMinutes(endTime);
    const boundaryEndTotalMinutes = toMinutes(boundaryEndTime ?? endTime);
    const isBoundaryEndInRange =
      endTotalMinutes > startTotalMinutes &&
      boundaryEndTotalMinutes >= startTotalMinutes &&
      boundaryEndTotalMinutes <= endTotalMinutes;

    if (!isBoundaryEndInRange) {
      return undefined;
    }

    return {
      time: formatTime(boundaryEndTotalMinutes),
      label: formatTimelineLabel(boundaryEndTotalMinutes, timeFormat),
      type: resolveMarkType(boundaryEndTotalMinutes),
      offsetPercent:
        ((boundaryEndTotalMinutes - startTotalMinutes) /
          (endTotalMinutes - startTotalMinutes)) *
        100,
    };
  }, [boundaryEndTime, endTime, startTime, timeFormat]);

  const timelineMarks = useMemo<TimelineMark[]>(() => {
    const marks: TimelineMark[] = [];
    const startTotalMinutes = toMinutes(startTime);
    const endTotalMinutes = toMinutes(endTime);
    const boundaryEndTotalMinutes = boundaryEndMark
      ? toMinutes(boundaryEndMark.time)
      : undefined;

    for (
      let totalMinutes = startTotalMinutes;
      totalMinutes < endTotalMinutes;
      totalMinutes += stepMinutes
    ) {
      const type = resolveMarkType(totalMinutes);
      const isFirstMark = marks.length === 0;
      const needsAnchorLabel = isFirstMark && type !== 'hour';
      const isCoveredByBoundaryEnd = totalMinutes === boundaryEndTotalMinutes;
      const regularLabel = needsAnchorLabel
        ? formatTimelineLabel(totalMinutes, timeFormat)
        : resolveMarkLabel(totalMinutes, type, timeFormat);
      const label = isCoveredByBoundaryEnd ? '' : regularLabel;

      marks.push({
        time: formatTime(totalMinutes),
        label,
        kind: isFirstMark ? 'boundary-start' : 'regular',
        type,
        isAnchorLabel: needsAnchorLabel,
      });
    }

    return marks;
  }, [boundaryEndMark, startTime, endTime, stepMinutes, timeFormat]);

  return {
    boundaryEndMark,
    timelineMarks,
  };
};
